import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import AdminLayout from '../components/AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDollarSign, 
  faPlus, 
  faCheckCircle,
  faExclamationCircle,
  faUndo,
  faCalendarAlt,
  faChevronDown,
  faEllipsisV,
  faEdit,
  faTrash,
  faCog,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import groupService from '../api/groupService';
import CreateDonationProductTypeModal from '../components/modals/CreateDonationProductTypeModal';
import EditDonationProductTypeModal from '../components/modals/EditDonationProductTypeModal';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import DonationTable from '../components/donations/DonationTable';
import { getCurrencySymbol } from '../utils/currencyUtils';
import config from '../config';
import '../styles/FinancialsPage.css';

const FinancialsPage = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [dateRange, setDateRange] = useState('all');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  
  // Data states
  const [donationProductTypes, setDonationProductTypes] = useState([]);
  const [donations, setDonations] = useState([]);
  const [eventTicketPurchases, setEventTicketPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProductType, setEditingProductType] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProductType, setDeletingProductType] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  // Stripe settings state
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showStripeDeleteModal, setShowStripeDeleteModal] = useState(false);
  
  // Ref to prevent overwriting recently created product types
  const lastCreatedProductType = useRef(null);
  
  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Determine active tab from URL path
  const activeTab = location.pathname.includes('/memberships') ? 'memberships' : 'payments';

  // Load data
  useEffect(() => {
    if (!adminGroup?.id) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchDonationProductTypes(),
          fetchDonations(),
          fetchEventTicketPurchases()
        ]);
      } catch (err) {
        console.error('Error loading financials data:', err);
        setError('Failed to load financial data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [adminGroup?.id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-range-dropdown')) {
        setShowDateDropdown(false);
      }
      if (openDropdownId !== null && !event.target.closest('.product-type-chip-container')) {
        setOpenDropdownId(null);
      }
      if (showSettingsDropdown && !event.target.closest('.settings-dropdown-container')) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateDropdown, openDropdownId, showSettingsDropdown]);

  // Fetch donation product types
  const fetchDonationProductTypes = async () => {
    try {
      const response = await groupService.getDonationProductTypes(adminGroup.id);
      
      if (response.success) {
        const serverData = response.data || [];
        
        // If we recently created an item, make sure it's included
        if (lastCreatedProductType.current) {
          const existsInServer = serverData.find(item => item.id === lastCreatedProductType.current.id);
          if (!existsInServer) {
            // Add the recently created item if it's not in the server response yet
            const finalData = [...serverData, lastCreatedProductType.current];
            setDonationProductTypes(finalData);
          } else {
            setDonationProductTypes(serverData);
            lastCreatedProductType.current = null; // Clear the ref
          }
        } else {
          setDonationProductTypes(serverData);
        }
      }
    } catch (err) {
      console.error('Error fetching donation product types:', err);
    }
  };

  // Fetch donations
  const fetchDonations = async (productTypeId = null, offset = 0) => {
    try {
      const params = {
        product_type_id: productTypeId || activeFilter,
        limit: pagination.limit,
        offset
      };
      
      const response = await groupService.getGroupDonations(adminGroup.id, params);
      if (response.success) {
        // Product type display now working correctly with manual JOIN logic
        // Currency is now stored in each donation object, not at response level
        
        if (offset === 0) {
          setDonations(response.data || []);
        } else {
          setDonations(prev => [...prev, ...(response.data || [])]);
        }
        setPagination(response.pagination || {});
      }
    } catch (err) {
      console.error('Error fetching donations:', err);
    }
  };

  // Fetch event ticket purchases
  const fetchEventTicketPurchases = async () => {
    try {
      const response = await groupService.getGroupEvents(adminGroup.id);
      if (response.success) {
        const events = response.data || [];
        // Fetch ticket purchases for each paid event
        const allPurchases = [];
        for (const event of events) {
          if (event.requires_payment) {
            try {
              const purchasesResponse = await groupService.getEventTicketPurchases(adminGroup.id, event.id);
              if (purchasesResponse.success && purchasesResponse.data) {
                // Add event details to each purchase
                purchasesResponse.data.forEach(purchase => {
                  allPurchases.push({
                    ...purchase,
                    event_title: event.title,
                    event_date: event.date
                  });
                });
              }
            } catch (err) {
              console.error(`Error fetching purchases for event ${event.id}:`, err);
            }
          }
        }
        setEventTicketPurchases(allPurchases);
      }
    } catch (err) {
      console.error('Error fetching event ticket purchases:', err);
    }
  };

  // Handle filter change
  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchDonations(filterId, 0);
  };

  // Handle date range change
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setShowDateDropdown(false);
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchDonations(activeFilter, 0);
  };

  // Get date range filter
  const getDateRangeFilter = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (range) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case 'last7days':
        return { start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), end: now };
      case 'last30days':
        return { start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), end: now };
      case 'last6months':
        return { start: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()), end: now };
      case 'thisyear':
        return { start: new Date(now.getFullYear(), 0, 1), end: now };
      default:
        return null;
    }
  };

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All time' },
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last30days', label: 'Last 30 days' },
    { value: 'last6months', label: 'Last 6 months' },
    { value: 'thisyear', label: 'This year' }
  ];

  // Handle product type creation with limit check
  const handleCreateProductType = () => {
    if (donationProductTypes.length >= 9) {
      alert('Maximum of 9 product types allowed. Please delete an existing product type before adding a new one.');
      return;
    }
    setShowCreateModal(true);
  };

  // Handle product type creation
  const handleProductTypeCreated = async (newProductType) => {
    // Store the newly created item in ref
    lastCreatedProductType.current = newProductType;
    
    // Update state immediately for instant feedback
    setDonationProductTypes(prev => [...prev, newProductType]);
    setShowCreateModal(false);
    
    // Clear the ref after some time to allow normal fetching
    setTimeout(() => {
      lastCreatedProductType.current = null;
    }, 3000);
  };

  // Handle product type editing
  const handleEditProductType = (productType) => {
    setEditingProductType(productType);
    setShowEditModal(true);
  };

  // Handle product type update
  const handleProductTypeUpdated = (updatedProductType) => {
    // Update the product type in the state
    setDonationProductTypes(prev => 
      prev.map(type => 
        type.id === updatedProductType.id ? updatedProductType : type
      )
    );
    setShowEditModal(false);
    setEditingProductType(null);
  };

  // Handle dropdown menu toggle
  const handleDropdownToggle = (typeId, event) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === typeId ? null : typeId);
  };

  // Handle delete product type
  const handleDeleteProductType = (productType) => {
    setDeletingProductType(productType);
    setShowDeleteModal(true);
    setOpenDropdownId(null);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!deletingProductType || deleteLoading) return;
    
    setDeleteLoading(true);
    
    try {
      await groupService.deleteDonationProductType(adminGroup.id, deletingProductType.id);
      
      // Remove from state
      setDonationProductTypes(prev => 
        prev.filter(type => type.id !== deletingProductType.id)
      );
      
      // If this was the active filter, reset to 'all'
      if (activeFilter === deletingProductType.id) {
        setActiveFilter('all');
      }
      
      // Close modal and reset states
      setShowDeleteModal(false);
      setDeletingProductType(null);
      setDeleteLoading(false);
    } catch (error) {
      console.error('Error deleting product type:', error);
      setDeleteLoading(false);
      // Handle error - you might want to show a toast notification
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    const newOffset = pagination.offset + pagination.limit;
    fetchDonations(activeFilter, newOffset);
  };

  // Stripe functionality handlers
  const handleOpenDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to access your dashboard');
        return;
      }

      const response = await fetch(`${config.API_URL}/stripe/dashboard_link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        window.open(data.url, '_blank');
      } else {
        // If the error indicates reconnection is needed, refresh admin group data
        if (data.requiresReconnection) {
          // Refresh admin group data to reflect cleared stripe account
          if (window.fetchAdminGroup && typeof window.fetchAdminGroup === 'function') {
            await window.fetchAdminGroup();
          }
        }
        throw new Error(data.error || 'Failed to get dashboard link');
      }
    } catch (error) {
      console.error('Error opening dashboard:', error);
      setError(error.message || 'Failed to open dashboard. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleFinishOnboarding = async () => {
    try {
      setLoading(true);
      setError('');

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const email = user.email;

      if (!adminGroup?.id) {
        throw new Error('No admin group found');
      }

      if (!email) {
        throw new Error('User email not found');
      }

      // If we already have a stripe account, just continue onboarding
      if (adminGroup?.stripe_account_id) {
        await createOnboardingLink(adminGroup.stripe_account_id);
      } else {
        // Create new account first, then onboarding link
        const response = await fetch(`${config.API_URL}/stripe/create-account`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            groupId: adminGroup.id,
            email: email
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create Stripe account');
        }

        // Now create onboarding link
        await createOnboardingLink(data.accountId);
      }
    } catch (error) {
      console.error('Error finishing onboarding:', error);
      setError(error.message || 'Failed to start onboarding. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const createOnboardingLink = async (accountId) => {
    const response = await fetch(`${config.API_URL}/stripe/onboarding-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        accountId: accountId,
        refreshUrl: `${window.location.origin}/admin/dashboard/financials/payments?stripe=refresh`,
        returnUrl: `${window.location.origin}/admin/dashboard/financials/payments?stripe=success`
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create onboarding link');
    }

    // Redirect to Stripe onboarding
    window.location.href = data.url;
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteLoading(true);
      setError('');

      const response = await fetch(`${config.API_URL}/stripe/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowStripeDeleteModal(false);
        // Refresh admin group to reflect changes
        if (adminGroup) {
          window.location.reload(); // Simple refresh to update state
        }
      } else {
        throw new Error(data.error || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setError(error.message || 'Failed to delete account. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter donations by search query and date range
  const filteredDonations = donations.filter(donation => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const user = donation.native_users || donation.users; // Support both user formats
      const userName = user?.full_name || (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '') || user?.name || '';
      const matchesSearch = (
        donation.dedication?.toLowerCase().includes(searchLower) ||
        userName.toLowerCase().includes(searchLower) ||
        user?.email?.toLowerCase().includes(searchLower) ||
        donation.donation_product_types?.name?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Date range filter
    if (dateRange !== 'all') {
      const dateFilter = getDateRangeFilter(dateRange);
      if (dateFilter) {
        const donationDate = new Date(donation.created_at);
        if (donationDate < dateFilter.start || donationDate > dateFilter.end) {
          return false;
        }
      }
    }

    return true;
  });

  // Sort donations
  const sortedDonations = [...filteredDonations].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'amount_high':
        return (b.amount_cents || 0) - (a.amount_cents || 0);
      case 'amount_low':
        return (a.amount_cents || 0) - (b.amount_cents || 0);
      default: // newest
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  // Calculate dynamic total based on filtered successful payments only
  const successfulFilteredDonations = sortedDonations.filter(d => d.status === 'succeeded');
  const filteredSuccessfulTotal = successfulFilteredDonations.reduce((sum, d) => sum + (d.amount_cents || 0), 0) / 100;

  // CSV Export function
  const handleExportCSV = () => {
    if (donations.length === 0) {
      alert('No donations to export');
      return;
    }

    // Prepare CSV headers
    const headers = [
      'Date',
      'Confirmation Number',
      'Donor Name',
      'Donor Email', 
      'Amount',
      'Currency',
      'Application Fee Covered',
      'Application Fee Amount',
      'Product Type',
      'Status',
      'Dedication Message',
      'Payment Method',
      'Transaction ID'
    ];

    // Prepare CSV rows
    const csvRows = donations.map(donation => {
      const donationCurrency = donation.currency || 'usd';
      const currencySymbol = getCurrencySymbol(donationCurrency);
      const date = new Date(donation.created_at).toLocaleDateString();
      const amount = `${currencySymbol}${(donation.amount_cents / 100).toFixed(2)}`;
      const applicationFeeCovered = donation.application_fee_covered ? 'Yes' : 'No';
      const applicationFeeAmount = donation.application_fee_covered 
        ? `${currencySymbol}${((donation.application_fee_cents || 0) / 100).toFixed(2)}` 
        : `${currencySymbol}0.00`;
      const productType = donation.donation_product_types?.name || donation.product_type_name || 'General Donation';
      const dedicationMessage = donation.dedication_message || '';
      const paymentMethod = donation.payment_method_types?.[0] || 'card';
      
      // Get donor info from native_users or users
      const user = donation.native_users || donation.users;
      const donorName = user?.full_name || (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '') || user?.name || 'Anonymous';
      const donorEmail = user?.email || '';
      
      // Escape commas and quotes in CSV data
      const escapeCSV = (field) => {
        if (field == null) return '';
        const str = String(field);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [
        escapeCSV(date),
        escapeCSV(donation.confirmation_number || ''),
        escapeCSV(donorName),
        escapeCSV(donorEmail),
        escapeCSV(amount),
        escapeCSV(donationCurrency.toUpperCase()),
        escapeCSV(applicationFeeCovered),
        escapeCSV(applicationFeeAmount),
        escapeCSV(productType),
        escapeCSV(donation.status),
        escapeCSV(dedicationMessage),
        escapeCSV(paymentMethod),
        escapeCSV(donation.stripe_payment_intent_id || donation.id)
      ].join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `donations-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render single summary box - dynamic total of successful payments
  const renderSummaryBoxes = () => {
    // Use the first successful donation's currency, fallback to 'usd'
    const firstSuccessfulDonation = donations.find(d => d.status === 'succeeded');
    const displayCurrency = firstSuccessfulDonation?.currency || 'usd';
    const currencySymbol = getCurrencySymbol(displayCurrency);
    
    return (
      <div className="summary-boxes">
        <div className="summary-box">
          <div className="summary-content">
            <div className="summary-left">
              <div className="summary-label">
                <FontAwesomeIcon icon={faCheckCircle} className="status-icon succeeded" />
                Total Successful Payments
              </div>
              <div className="summary-value">{currencySymbol}{filteredSuccessfulTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          <div className="stripe-actions">
            {(adminGroup?.onboarding_status === 'active' && adminGroup?.stripe_account_id) ? (
              <button 
                className="open-dashboard-button"
                onClick={handleOpenDashboard}
              >
                Open Dashboard
              </button>
            ) : (
              <button 
                className="finish-onboarding-button"
                onClick={handleFinishOnboarding}
              >
                Complete Setup
              </button>
            )}
            <div className="settings-dropdown-container">
              <button 
                className="settings-button" 
                aria-label="Settings"
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              >
                <FontAwesomeIcon icon={faCog} />
              </button>
              {showSettingsDropdown && (
                <div className="settings-dropdown">
                  <button 
                    className="dropdown-item delete-item"
                    onClick={() => {
                      setShowSettingsDropdown(false);
                      setShowStripeDeleteModal(true);
                    }}
                  >
                    Delete Account
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  // Render chip-style filters
  const renderChipFilters = () => (
    <div className="chip-filters-container">
      <div className="chip-filters">
        <button
          className={`chip-filter ${activeFilter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
        </button>
        {donationProductTypes.map(type => (
          <div key={type.id} className="product-type-chip-container">
            <button
              className={`chip-filter ${activeFilter === type.id ? 'active' : ''} chip-with-menu`}
              onClick={() => handleFilterChange(type.id)}
            >
              <span>{type.name}</span>
              <div className="chip-menu-container">
                <div
                  className="chip-menu-button"
                  onClick={(e) => handleDropdownToggle(type.id, e)}
                  title="More options"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleDropdownToggle(type.id, e);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </div>
                {openDropdownId === type.id && (
                  <div className="chip-dropdown-menu">
                    <button
                      className="chip-dropdown-item"
                      onClick={() => {
                        handleEditProductType(type);
                        setOpenDropdownId(null);
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      Edit
                    </button>
                    <button
                      className="chip-dropdown-item delete"
                      onClick={() => handleDeleteProductType(type)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </button>
          </div>
        ))}
        <button
          className={`chip-filter add-chip ${donationProductTypes.length >= 9 ? 'disabled' : ''}`}
          onClick={handleCreateProductType}
          title={donationProductTypes.length >= 9 ? "Maximum 9 product types allowed" : "Add new product type"}
          disabled={donationProductTypes.length >= 9}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
      
      {/* Right side controls - Export and Date Picker */}
      <div className="right-controls">
        {/* Export Link */}
        <button
          className="export-link"
          onClick={handleExportCSV}
          title="Export donations to CSV"
          disabled={donations.length === 0}
        >
          <FontAwesomeIcon icon={faDownload} />
          <span>Export</span>
        </button>
        
        {/* Date Range Picker */}
        <div className="date-range-picker">
          <div className="date-range-dropdown">
            <button
              className={`date-range-button ${dateRange !== 'all' ? 'active' : ''}`}
              onClick={() => setShowDateDropdown(!showDateDropdown)}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              {dateRangeOptions.find(option => option.value === dateRange)?.label || 'All time'}
              <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
            </button>
            
            {showDateDropdown && (
              <div className="date-range-menu">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`date-range-option ${dateRange === option.value ? 'active' : ''}`}
                    onClick={() => handleDateRangeChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render donations table
  const renderDonationsTable = () => (
    <DonationTable
      donations={sortedDonations}
      onLoadMore={handleLoadMore}
      hasMore={pagination.hasMore}
      loading={loading}
    />
  );

  // Render event tickets table
  const renderEventTicketsTable = () => {
    if (eventTicketPurchases.length === 0) {
      return (
        <div className="empty-state">
          <p>No event tickets have been purchased yet.</p>
        </div>
      );
    }

    return (
      <div className="donation-table-container">
        <div className="donation-table">
          <table>
            <thead>
              <tr>
                <th>Event</th>
                <th>Purchaser</th>
                <th>Tickets</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {eventTicketPurchases.map((purchase) => {
                const user = purchase.users || purchase.native_users;
                const userName = user?.full_name || 
                  (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '') ||
                  user?.name || 
                  'Anonymous';
                const userEmail = user?.email || '';

                return (
                  <tr key={purchase.id}>
                    <td>
                      <div className="donor-info">
                        <div className="donor-name">{purchase.event_title}</div>
                        <div className="donor-email text-muted">
                          {new Date(purchase.event_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="donor-info">
                        <div className="donor-name">{userName}</div>
                        {userEmail && <div className="donor-email text-muted">{userEmail}</div>}
                      </div>
                    </td>
                    <td>
                      <span className="text-gray-900">{purchase.attendee_count}</span>
                    </td>
                    <td>
                      <div className="font-medium text-gray-900">
                        ${(purchase.total_amount_cents / 100).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        ${(purchase.price_per_ticket_cents / 100).toFixed(2)} per ticket
                      </div>
                    </td>
                    <td>
                      <div className="text-gray-900">
                        {new Date(purchase.succeeded_at || purchase.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(purchase.succeeded_at || purchase.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td>
                      <div 
                        className="status-badge" 
                        style={{ 
                          color: purchase.payment_status === 'succeeded' ? '#10b981' : '#f59e0b',
                          backgroundColor: purchase.payment_status === 'succeeded' ? '#ecfdf5' : '#fffbeb'
                        }}
                      >
                        <FontAwesomeIcon 
                          icon={purchase.payment_status === 'succeeded' ? faCheckCircle : faExclamationCircle}
                        />
                        <span>{purchase.payment_status === 'succeeded' ? 'Completed' : 'Pending'}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render empty state
  const renderEmptyState = () => {
    if (activeFilter === 'all' && donations.length === 0) {
      return (
        <div className="empty-state">
          <p>No donations have been made yet.</p>
        </div>
      );
    }

    if (activeFilter !== 'all' && sortedDonations.length === 0) {
      return (
        <div className="empty-state">
          <p>No donations have been made yet.</p>
        </div>
      );
    }

    return null;
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="financials-page">
          <div className="financials-container">
            <div className="page-header">
              <h1>{activeTab === 'payments' ? 'Payments' : 'Memberships'}</h1>
            </div>
            <div className="loading-state">Loading...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AdminLayout>
        <div className="financials-page">
          <div className="financials-container">
            <div className="page-header">
              <h1>{activeTab === 'payments' ? 'Donations' : 'Memberships'}</h1>
            </div>
            <div className="error-state">Error: {error}</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="financials-page">
        <div className="financials-container">
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-top">
              <h1>{activeTab === 'payments' ? 'Donations' : 'Memberships'}</h1>
              {activeTab === 'payments' && (
                <button 
                  className={`add-donation-type-button ${donationProductTypes.length >= 9 ? 'disabled' : ''}`}
                  onClick={handleCreateProductType}
                  disabled={donationProductTypes.length >= 9}
                >
                  {donationProductTypes.length >= 9 ? 'Maximum Types Reached' : 'Add Donation Type'}
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'payments' ? (
            <>
              {/* Summary Boxes */}
              {renderSummaryBoxes()}

              {/* Chip Filters */}
              {renderChipFilters()}

              {/* Donations Table */}
              {renderDonationsTable()}
            </>
          ) : activeTab === 'memberships' ? (
            <div className="coming-soon-page">
              <div className="coming-soon-content">
                <h2 className="coming-soon-title">Memberships</h2>
                <p className="coming-soon-description">
                  Manage your community memberships, subscription plans, and member benefits.
                </p>
                <div className="coming-soon-badge">Coming Soon</div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Modals */}
        {showCreateModal && (
          <CreateDonationProductTypeModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            groupId={adminGroup?.id}
            onProductTypeCreated={handleProductTypeCreated}
          />
        )}
        
        {showEditModal && (
          <EditDonationProductTypeModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setEditingProductType(null);
            }}
            groupId={adminGroup?.id}
            productType={editingProductType}
            onProductTypeUpdated={handleProductTypeUpdated}
          />
        )}
        
        {showDeleteModal && (
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => {
              if (!deleteLoading) {
                setShowDeleteModal(false);
                setDeletingProductType(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            loading={deleteLoading}
            title="Delete Donation Type"
            message="Are you sure you want to delete the donation type"
            itemName={deletingProductType?.name}
          />
        )}
        
        {/* Stripe Delete Account Confirmation Modal */}
        {showStripeDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowStripeDeleteModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Stripe Account</h3>
              <div className="modal-body">
                <p><strong>⚠️ Warning: This action cannot be undone!</strong></p>
                <p>Deleting your Stripe account will:</p>
                <ul>
                  <li>Permanently remove your payment processing capabilities</li>
                  <li>Stop all future payments from members</li>
                  <li>Remove access to payout history and balance information</li>
                  <li>Require you to set up a new Stripe account to accept payments again</li>
                </ul>
                <p>Are you absolutely sure you want to delete your entire Stripe account?</p>
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => setShowStripeDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="delete-button"
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : 'Yes, Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FinancialsPage;